import axios, { AxiosRequestConfig } from 'axios';
import merge from 'deepmerge';
import apiConfig from './apiConfig';
import AuthProtect from 'common/enums/AuthProtect';
import { store } from 'store';
import { IEndpoint } from 'interfaces/IEndPoint';

const genericErrorMessage =
  'We just experienced a technical issue, please wait a few seconds and try again.';

/**
 * Fetches data from an API endpoint using Axios.
 *
 * @template T - The expected type of the response data.
 * @param {IEndpoint} api - The API endpoint configuration.
 * @returns {Promise<{ status: number; data: T }>} - A promise resolving to the response status and data.
 */
function apiFetch<T>(api: IEndpoint): Promise<{ status: number; data: T }> {
  const headers: AxiosRequestConfig = {};
  // eslint-disable-next-line
  const params: any = api.params || {}; // Default to an empty object if no params are provided.
  const baseURL = apiConfig.baseURL;

  // Add authorization token to params if required.
  if (api.authorization === AuthProtect.REQUIRED) {
    const token = store.getState().token.authorization;
    if (token) {
      params.access_token = token;
    }
  }

  // Merge the API configuration with the provided endpoint configuration.
  const apiConfigMerge: AxiosRequestConfig = merge.all([
    apiConfig,
    api,
    {
      baseURL,
      headers,
      params,
    },
  ]);

  // Create an Axios instance with the merged configuration.
  const axiosCreate = axios.create(apiConfigMerge);

  // Add a response interceptor to handle responses globally.
  axiosCreate.interceptors.response.use(
    function (response) {
      //console.log('responseUrl >>>' + response.request.responseURL + ' response.status >>>' + response.status);
      return response;
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  // Make the API request and handle validation, errors, and responses.
  return axiosCreate({
    ...apiConfigMerge,
    validateStatus: (status: number) => {
      // Clear local storage and redirect to login on unauthorized or forbidden status.
      if (status === 401 || status === 403) {
        localStorage.clear();
        window.location.href = '/login';
      }
      return status < 500; // Accept all statuses below 500.
    },
  })
    .catch((e) => {
      console.log('apiFetch catch:', e);
      // Handle network errors by clearing local storage and redirecting to login.
      if (e instanceof Error) {
        if (e.message === 'Network Error') {
          localStorage.clear();
          window.location.href = '/login';
        }
      }
      // Throw a generic error message for other cases.
      throw {
        message: genericErrorMessage,
      };
    })
    .then((response) => {
      // Handle unauthorized or forbidden responses.
      if (response.status === 401 || response.status === 403) {
        localStorage.clear();
        window.location.href = '/login';
      }

      // Return the response status and data.
      return {
        status: response.status,
        data: response.data,
      };
    });
}

export default apiFetch;
